import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLang } from "../context/LanguageContext";
import { PageHeader } from "../components/common/PageHeader";
import { DataTable } from "../components/common/DataTable";
import { StatusBadge } from "../components/common/StatusBadge";
import { SampleBadge } from "../components/common/SampleBadge";
import { Pick } from "../components/loans/LoanFormDialog";
import { RepaymentFormDialog } from "../components/repayments/RepaymentFormDialog";
import { ReceiptDialog } from "../components/repayments/ReceiptDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { CLFs, VOs, SHGs, Members, Loans, Repayments } from "../lib/api";
import { sampleRepayments } from "../lib/sampleData";
import { formatINR, formatIndianDate } from "../lib/format";
import { modeLabel, printReceipt } from "../lib/receipt";
import { toast } from "sonner";
import { Plus, Eye, Printer, X } from "lucide-react";

const NOF = { clf_id: "all", vo_id: "all", shg_id: "all", loan_number: "", date_from: "", date_to: "", status: "all" };
const byId = (list) => Object.fromEntries(list.map((x) => [x.id, x.name]));
const STATUS_TEXT = (r) => (r.status === "reversed" ? "Reversed" : r.coverage);

const RepaymentsPage = () => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [refs, setRefs] = useState({ clfs: [], vos: [], shgs: [], members: [] });
  const [loans, setLoans] = useState([]);
  const [f, setF] = useState(NOF);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [viewId, setViewId] = useState(null);
  const [showDemo, setShowDemo] = useState(false);

  const loadRefs = useCallback(async () => {
    const [clfs, vos, shgs, members, l] = await Promise.all([CLFs.list(), VOs.list(), SHGs.list(), Members.list(), Loans.list()]);
    setRefs({ clfs, vos, shgs, members }); setLoans(l);
  }, []);
  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "all"));
      setRows(await Repayments.list(params));
    } catch (e) { toast.error(e.message || "Failed to load repayments"); } finally { setLoading(false); }
  }, [f]);
  useEffect(() => { loadRefs().catch(() => toast.error("Failed to load records")); }, [loadRefs]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const names = useMemo(() => ({ clf: byId(refs.clfs), vo: byId(refs.vos), shg: byId(refs.shgs), member: byId(refs.members) }), [refs]);
  const table = useMemo(() => rows.map((r) => ({ ...r, shgName: names.shg[r.shg_id] || "", memberName: r.member_id ? names.member[r.member_id] || "" : "",
    statusText: STATUS_TEXT(r), modeText: modeLabel(r.mode) })), [rows, names]);
  const setFilter = (k, v) => setF((p) => ({ ...p, [k]: v, ...(k === "clf_id" ? { vo_id: "all", shg_id: "all" } : k === "vo_id" ? { shg_id: "all" } : {}) }));
  const sel = (list, all) => [{ value: "all", label: all }, ...list.map((x) => ({ value: x.id, label: x.name }))];

  const refresh = async () => { await Promise.all([loadRows(), loadRefs()]); };
  const onSaved = async (rep) => {
    toast.success(`Receipt ${rep.receipt_number} saved · outstanding now ${formatINR(rep.outstanding_after)}`);
    await refresh(); setViewId(rep.id);
  };

  const columns = [
    { key: "receipt_number", header: "Receipt no.", mono: true, strong: true },
    { key: "payment_date", header: "Date", mono: true, render: (r) => formatIndianDate(r.payment_date) },
    { key: "loan_number", header: "Loan ID", mono: true },
    { key: "shgName", header: "SHG" },
    { key: "memberName", header: "Member", render: (r) => r.memberName || <span className="text-emerald-900/50">SHG</span> },
    { key: "amount", header: "Amount", align: "right", mono: true, render: (r) => <span className={r.status === "reversed" ? "line-through text-emerald-900/40" : ""}>{formatINR(r.amount)}</span> },
    { key: "modeText", header: "Mode" },
    { key: "collected_by", header: "Collector" },
    { key: "statusText", header: "Status", render: (r) => <StatusBadge status={r.statusText} /> },
    { key: "_a", header: "Actions", render: (r) => (
      <div className="flex gap-1.5">
        <Button size="sm" variant="outline" className="h-8 px-2" data-testid={`repayment-view-${r.id}`} onClick={() => setViewId(r.id)}><Eye className="h-3.5 w-3.5" /></Button>
        <Button size="sm" variant="outline" className="h-8 px-2" data-testid={`repayment-print-${r.id}`}
          onClick={() => !printReceipt(r, names) && toast.error("Allow pop-ups to print receipts")}><Printer className="h-3.5 w-3.5" /></Button>
      </div>
    ) },
  ];
  const demoColumns = [
    { key: "id", header: "Demo receipt", mono: true }, { key: "loan", header: "Loan", mono: true }, { key: "shg", header: "SHG" },
    { key: "date", header: "Date", mono: true }, { key: "amount", header: "Amount", align: "right", mono: true, render: (r) => formatINR(r.amount) },
    { key: "mode", header: "Mode" }, { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];
  const hasActiveLoans = loans.some((l) => l.status === "active");

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader title={t.nav.repayments} subtitle="Repayments saved in your database" testId="repayments-page-header" showSampleBadge={false}
        actions={<Button data-testid="repayment-add-btn" disabled={!hasActiveLoans} onClick={() => setFormOpen(true)} className="bg-[#0F4C3A] text-white hover:bg-emerald-900">
          <Plus className="mr-1.5 h-4 w-4" />Record Repayment</Button>} />

      {!hasActiveLoans && <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="repayment-setup-notice">
        Add at least one active loan before recording repayments.</div>}

      <div className="mb-4 grid grid-cols-2 gap-3 rounded-2xl border border-emerald-950/10 bg-white p-4 md:grid-cols-4 lg:grid-cols-8" data-testid="repayment-filters">
        <Pick testId="filter-clf" value={f.clf_id} options={sel(refs.clfs, "All CLFs")} onChange={(v) => setFilter("clf_id", v)} />
        <Pick testId="filter-vo" value={f.vo_id} options={sel(refs.vos.filter((x) => f.clf_id === "all" || x.clf_id === f.clf_id), "All VOs")} onChange={(v) => setFilter("vo_id", v)} />
        <Pick testId="filter-shg" value={f.shg_id} options={sel(refs.shgs.filter((x) => f.vo_id === "all" || x.vo_id === f.vo_id), "All SHGs")} onChange={(v) => setFilter("shg_id", v)} />
        <Input placeholder="Loan ID" value={f.loan_number} onChange={(e) => setFilter("loan_number", e.target.value)} className="h-10" data-testid="filter-loan" />
        <Input type="date" title="From date" value={f.date_from} onChange={(e) => setFilter("date_from", e.target.value)} className="h-10" data-testid="filter-from" />
        <Input type="date" title="To date" value={f.date_to} onChange={(e) => setFilter("date_to", e.target.value)} className="h-10" data-testid="filter-to" />
        <Pick testId="filter-status" value={f.status} onChange={(v) => setFilter("status", v)} options={[{ value: "all", label: "All statuses" },
          { value: "Paid", label: "Paid" }, { value: "Partially Paid", label: "Partially Paid" }, { value: "reversed", label: "Reversed" }]} />
        <Button variant="outline" className="h-10" onClick={() => setF(NOF)} data-testid="filter-clear"><X className="mr-1 h-4 w-4" />Clear</Button>
      </div>

      {loading ? <div className="rounded-2xl border border-emerald-950/10 bg-white p-10 text-center text-sm text-emerald-900/50">Loading…</div> : (
        <DataTable columns={columns} rows={table} searchKeys={["receipt_number", "loan_number", "shgName", "memberName", "collected_by", "reference", "modeText", "statusText"]}
          testId="repayments-table" emptyLabel="No repayments match. Click “Record Repayment” to add one." />
      )}

      <div className="mt-8 rounded-2xl border border-dashed border-[#D95D39]/40 p-4 sample-data-stripe" data-testid="repayments-demo-section">
        <label className="flex items-center gap-3 text-sm text-emerald-900/80">
          <Switch checked={showDemo} onCheckedChange={setShowDemo} data-testid="repayments-demo-toggle" />
          Show demo records <SampleBadge /><span className="text-xs text-emerald-900/50">Not real data · not saved · excluded from totals</span>
        </label>
        {showDemo && <div className="mt-4"><DataTable columns={demoColumns} rows={sampleRepayments} searchKeys={["id", "loan", "shg"]} testId="repayments-demo-table" /></div>}
      </div>

      <RepaymentFormDialog open={formOpen} onOpenChange={setFormOpen} refs={refs} loans={loans} onSubmit={Repayments.create} onSaved={onSaved} />
      {viewId && <ReceiptDialog repId={viewId} names={names} onOpenChange={() => setViewId(null)} onReversed={refresh} />}
    </div>
  );
};

export default RepaymentsPage;
